import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import "../css/DailyPunchedAttendence.css";
import { format } from "date-fns";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import SmsModal from "../Admin/DailyPunchSheets/SmsModal";
import EmployeeMapTracker from "../../src/pages/EmployeeMapTracker";
import RedFlag from "../images/RedFlag.png";
import GreenFlag from "../images/GreenFlag.png";
import Loader from "../dist/Loader";
import { CheckCircle } from "lucide-react";
import { formatDate } from "../utils/formatDate";
import { formatTimeTo12Hour } from "../utils/formatTime";
import CustomButton from "../pages/CustomButton";

const AbsentDailyPunch = () => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [error, setError] = useState(null);
  const [smsModal, setSmsModal] = useState({ isOpen: false, employee: null });
  const location = useLocation();
  const [mapModal, setMapModal] = useState({
    isOpen: false,
    employeeData: null,
  });

  const tableWrapperRef = useRef(null);

  // Parse URL parameters
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      state_id: params.get("state_id"),
      division: params.get("division"),
      employee_id: params.get("employee_id"),
      quarter: params.get("quarter"),
      is_absent: params.get("is_absent"),
    };
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const dt = new Date(date);
    return dt.toISOString().split("T")[0];
  };

  const formatDateTime = (dateString, displayTimeOnly = false) => {
    if (!dateString) return "-";

    const date = new Date(
      dateString.endsWith("Z") ? dateString : `${dateString}Z`,
    );

    const timeOptions = {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    const dateOptions = {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    if (displayTimeOnly) {
      return date.toLocaleTimeString("en-IN", timeOptions);
    }

    const formattedDate = date.toLocaleDateString("en-IN", dateOptions);
    const formattedTime = date.toLocaleTimeString("en-IN", timeOptions);

    return `${formattedDate} ${formattedTime}`;
  };

  // Load absent employees data
  const loadAbsentEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const urlParams = getUrlParams();
      const params = new URLSearchParams();

      if (urlParams.state_id && urlParams.state_id !== "All") {
        params.set("state_id", urlParams.state_id);
      }
      if (urlParams.division && urlParams.division !== "All") {
        params.set("division", urlParams.division);
      }
      if (urlParams.employee_id && urlParams.employee_id !== "All") {
        params.set("employee_id", urlParams.employee_id);
      }
      if (urlParams.quarter) {
        params.set("quarter", urlParams.quarter);
      }

      const url = `${BASE_URL}/api/pob-targets/pob_target_report?${params.toString()}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });

      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      // Transform and filter for absent employees
      const transformedData = responseData
        .map((item, index) => {
          const getFieldValue = (...fieldNames) => {
            for (const fieldName of fieldNames) {
              if (item[fieldName] !== undefined && item[fieldName] !== null)
                return item[fieldName];
            }
            return "";
          };

          const employeeName =
            getFieldValue("employee_name", "employee") ||
            `${getFieldValue("first_name") || ""} ${
              getFieldValue("last_name") || ""
            }`.trim();

          const morningStatus =
            getFieldValue(
              "mor_status",
              "morning_status",
              "attendance_status",
            ) || "Present";
          const eveningStatus =
            getFieldValue("eve_status", "evening_status") || "Present";

          return {
            serialNumber: index + 1,
            employeeId: getFieldValue("employee_id", "id"),
            employeeCode: getFieldValue("employee"),
            employeeName,
            designation: getFieldValue("designation", "desig") || "",
            division: getFieldValue("division") || "",
            state: getFieldValue("state", "state_name") || "",
            mandatoryCanLogin: getFieldValue("location_active_flag"),

            substate:
              getFieldValue("substate", "sub_state", "zone", "division_zone") ||
              "",
            headquarter:
              getFieldValue("hq", "headquarter", "headquarter_name") || "",
            final_joining_date:
              getFieldValue("final_joining_date", "joining_date", "doj") || "",
            manager: getFieldValue("manager", "manager_name") || "",
            visits: Number(getFieldValue("visits", "visits_count")) || 0,
            pob:
              Number(getFieldValue("pob", "pob_value", "total", "oct_pob")) ||
              0,
            locationCount:
              Number(getFieldValue("location_count", "locations_count")) || 0,
            gpsStop: getFieldValue("gps_stop", "gps_location", "gpsStop") || "",
            latestLocation:
              getFieldValue(
                "latest_location",
                "current_location",
                "location",
                "latestLocation",
              ) || "",
            gps_stop_idle_time:
              getFieldValue("gps_stop_idle_time", "gps_stop_idle") || "",
            morningFieldBy:
              item.morning_fld_by ??
              item.mor_field_by ??
              item.mor_fld_by ??
              "-",
            eveningFieldBy:
              item.evening_fld_by ??
              item.eve_field_by ??
              item.eve_fld_by ??
              "-",
            morningPunchTime:
              getFieldValue("mor_punch_time", "mor_punch", "morning_in_time") ||
              "",
            eveningPunchTime:
              getFieldValue(
                "eve_punch_time",
                "eve_punch",
                "evening_punch_time",
              ) || "",
            firstCallTime: getFieldValue("first_call_time") || "",
            lastCallTime: getFieldValue("last_call_time") || "",
            morningStatus,
            eveningStatus,
            residenceAddress:
              getFieldValue("address", "residence_address") || "",
            phone: getFieldValue("phone_no", "phone_number", "phone") || "",
            smsStatus: getFieldValue("sms") || "No",
          };
        })
        .filter((record) => {
          // Filter only absent employees (both morning and evening absent)
          return (
            record.morningStatus === "Absent" &&
            record.eveningStatus === "Absent"
          );
        })
        .map((record, index) => ({
          ...record,
          serialNumber: index + 1, // Re-number after filtering
        }));

      setTableData(transformedData);
      
    } catch (err) {
      console.error("Error fetching absent employees:", err);
      setError("Could not load absent employees. Please try again.");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openWorkingWithWindow = (employeeId) => {
    const urlParams = getUrlParams();
    const quarterDate = urlParams.quarter || format(new Date(), "dd/MMM/yyyy");
    const dateParts = quarterDate.split("/");

    let month, year;
    if (dateParts.length === 3) {
      const dateObj = new Date(
        `${dateParts[1]} ${dateParts[0]}, ${dateParts[2]}`,
      );
      month = dateObj.getMonth() + 1;
      year = dateObj.getFullYear();
    } else {
      month = format(new Date(), "M");
      year = format(new Date(), "yyyy");
    }

    const params = new URLSearchParams();
    if (urlParams.state_id) params.append("state_id", urlParams.state_id);
    params.append("month", month);
    params.append("year", year);
    params.append("employee_id", String(employeeId));

    window.open(
      `/admin/working-with?${params.toString()}`,
      "_blank",
      "width=1400,height=700",
    );
  };

  const openDcrPreviewWindow = (employeeId) => {
    const urlParams = getUrlParams();
    const quarterDate = urlParams.quarter || format(new Date(), "dd/MMM/yyyy");
    const dateParts = quarterDate.split("/");

    let dateString;
    if (dateParts.length === 3) {
      const dateObj = new Date(
        `${dateParts[1]} ${dateParts[0]}, ${dateParts[2]}`,
      );
      dateString = formatDateForApi(dateObj);
    } else {
      dateString = formatDateForApi(new Date());
    }

    window.open(
      `/admin/dcrpreviewtable?employee_id=${employeeId}&date=${dateString}`,
      "_blank",
      "width=1200,height=700",
    );
  };

  const openLocationHistoryWindow = (employeeId, employeeName) => {
    const urlParams = getUrlParams();
    const quarterDate = urlParams.quarter || format(new Date(), "dd/MMM/yyyy");
    const dateParts = quarterDate.split("/");

    let dateString;
    if (dateParts.length === 3) {
      const dateObj = new Date(
        `${dateParts[1]} ${dateParts[0]}, ${dateParts[2]}`,
      );
      dateString = formatDateForApi(dateObj);
    } else {
      dateString = formatDateForApi(new Date());
    }

    const params = new URLSearchParams();
    params.set("employee_id", employeeId);
    params.set("employee_name", encodeURIComponent(employeeName || ""));
    params.set("track_date", dateString);

    window.open(
      `/reporting/location-history?${params.toString()}`,
      "_blank",
      "width=1400,height=700",
    );
  };
const getTrackDateFromUrl = () => {
  const urlParams = getUrlParams();
  const quarter = urlParams.quarter;

  if (!quarter) {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  }

  const parts = quarter.split("/"); // ["11", "Jun", "2026"]

  if (parts.length === 3) {
    // "Jun 11 2026" parsed as LOCAL time (no UTC shift)
    const dateObj = new Date(`${parts[1]} ${parts[0]} ${parts[2]}`);

    if (!isNaN(dateObj.getTime())) {
      // ✅ Use local date methods, NOT toISOString()
      const yyyy = dateObj.getFullYear();
      const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
      const dd = String(dateObj.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};

 const openMapModal = (employeeData, context = "general") => {
  const trackDate = getTrackDateFromUrl();

  setMapModal({
    isOpen: true,
    employeeData: {
      employeeId: String(employeeData.employeeId),
      employeeCode: employeeData.employeeCode,
      employeeName: employeeData.employeeName,
      trackDate,
      context,
      state: employeeData.state,
      division: employeeData.division,
      designation: employeeData.designation,
      residenceAddress: employeeData.residenceAddress,
      specificPunchTime: employeeData.specificPunchTime || null,
    },
  });
};
  const closeMapModal = () => {
    setMapModal({ isOpen: false, employeeData: null });
  };

  const handleMorningPunchMap = (employeeData) => {
    openMapModal(
      { ...employeeData, specificPunchTime: employeeData.morningPunchTime },
      "morningPunch",
    );
  };

  const handleEveningPunchMap = (employeeData) => {
    openMapModal(
      { ...employeeData, specificPunchTime: employeeData.eveningPunchTime },
      "eveningPunch",
    );
  };

  const handleViewMap = (employeeData) => {
    openMapModal(employeeData, "viewMap");
  };

  const openSmsModal = (employee) => setSmsModal({ isOpen: true, employee });

  useEffect(() => {
    loadAbsentEmployees();
  }, [location.search]);

  const printableStyles = `
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial; font-size:12px; color:#111; }
      table { border-collapse: collapse; width: 100%; table-layout: fixed; word-break: break-word; }
      th, td { border: 1px solid #999; padding: 6px 8px; vertical-align: middle; }
      thead th { background: #dc2626; color: #fff; font-weight: 600; text-align: center; }
      .bg-red-100 { background-color: #fee2e2; }
      .text-center { text-align: center; }
      .text-left { text-align: left; }
      .nowrap { white-space: nowrap; }
      .small { font-size: 11px; }
      a { color: #1d4ed8; text-decoration: underline; }
    </style>
  `;

  const getPrintableTableHtml = () => {
    if (!tableWrapperRef.current) return "<div>No table found</div>";
    const wrapper = tableWrapperRef.current.cloneNode(true);
    wrapper.querySelectorAll("button").forEach((btn) => {
      const span = document.createElement("span");
      span.innerHTML = btn.innerHTML;
      btn.parentNode.replaceChild(span, btn);
    });
    return wrapper.innerHTML;
  };

  const footerTotals = tableData.reduce(
    (acc, row) => {
      acc.totalVisits += Number(row.visits || 0);
      acc.totalPob += Number(row.pob || 0);
      const punched =
        Boolean(row.morningPunchTime) || Boolean(row.eveningPunchTime);
      if (punched) acc.punch++;
      else acc.noPunch++;
      return acc;
    },
    {
      totalVisits: 0,
      totalPob: 0,
      punch: 0,
      noPunch: 0,
    },
  );

  const handlePrint = () => {
    try {
      const tableHtml = getPrintableTableHtml();
      const w = window.open("", "_blank", "width=1200,height=900");
      if (!w) {
        alert("Please allow popups to enable printing.");
        return;
      }
      w.document.open();
      w.document.write(
        `<html><head><title>Absent Employees Report</title>${printableStyles}</head><body>`,
      );
      w.document.write(`<div style="margin:12px 8px;">`);
      w.document.write(
        `<h2 style="margin-bottom:8px;">Absent Employees Report</h2>`,
      );
      w.document.write(tableHtml);
      w.document.write(`</div>`);
      w.document.write("</body></html>");
      w.document.close();
      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch (e) {
          console.error("Print failed", e);
        }
      }, 500);
    } catch (err) {
      console.error("Print error", err);
      alert("Unable to print. Check console for details.");
    }
  };

  const handleExportExcel = () => {
    try {
      if (!tableWrapperRef.current) {
        alert("No table to export");
        return;
      }
      const tableHtml = getPrintableTableHtml();
      const fullHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
          <head>
            <meta charset="utf-8" />
            <meta name="ProgId" content="Excel.Sheet" />
            <meta name="Generator" content="AbsentDailyPunch" />
            ${printableStyles}
          </head>
          <body>
            <div>
              <h2>Absent Employees Report</h2>
              ${tableHtml}
            </div>
          </body>
        </html>`;
      const blob = new Blob([fullHtml], {
        type: "application/vnd.ms-excel;charset=utf-8",
      });
      const filename = `absent_employees_${format(
        new Date(),
        "yyyyMMdd_HHmmss",
      )}.xls`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 10000);
    } catch (err) {
      console.error("Export error", err);
      alert("Unable to export. Check console for details.");
    }
  };

  return (
    <>
      <style>{`
        .dpa-loading-overlay {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,0.35);
          z-index: 9999;
        }
        .dpa-loader-wrapper {
          background: #fff;
          padding: 18px;
          border-radius: 10px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>

    {isLoading && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
    <Loader onCancel={() => setIsLoading(false)} />
  </div>
)}

      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
        <div className="p-4 border-2 border-red-300 rounded-lg bg-red-50">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-red-700">Absent </span>
            <span className="text-red-500">Employees Report</span>
          </h1>
          {error && (
            <div className="text-red-600 text-center mb-4">{error}</div>
          )}
        </div>

        {tableData.length > 0 && (
          <div
            className="table-wrapper mt-6 border-2 border-gray-300 rounded-lg bg-white p-3"
            ref={tableWrapperRef}
          >
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-red-600 text-white">
                <tr>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    S.No
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    Emp ID
                  </th>
                  <th className="border px-2 py-2 text-center align-middle">
                    Employee
                  </th>
                  <th className="border px-2 py-2 text-center align-middle">
                    Desig
                    <br />
                    Div
                  </th>
                  <th className="border px-2 py-2 text-center align-middle">
                    State
                    <br />
                    Sub State
                    <br />
                    HQ
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    DOJ
                  </th>
                  <th className="border px-2 py-2 text-center align-middle">
                    Manager
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    MANDATORY
                    <br />
                    Can Login
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    Visits
                    <br />
                    POB
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    Location
                    <br />
                    Count
                    <br />
                    GPS Stop
                  </th>
                  <th className="border px-2 py-2 text-center align-middle w-64 whitespace-normal">
                    Latest
                    <br />
                    Location
                  </th>
                  <th className="border px-2 py-2 text-center align-middle bg-red-200 whitespace-nowrap">
                    Mor Punch Time
                    <br />
                    Eve Punch Time
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    First Call Time
                    <br />
                    Last Call Time
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    Mor Fld By
                    <br />
                    Eve Fld By
                  </th>
                  <th className="border px-2 py-2 text-center align-middle">
                    Mor Status
                    <br />
                    Eve Status
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-normal">
                    Residence Address
                  </th>
                  <th className="border px-2 py-2 text-center align-middle whitespace-nowrap">
                    SMS
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData.map((record, index) => (
                  <tr
                    key={`${record.employeeId}-${index}`}
                    className={index % 2 === 0 ? "bg-white" : "bg-red-50"}
                  >
                    <td className="border px-2 py-3 text-center align-middle whitespace-nowrap">
                      {record.serialNumber}
                    </td>
                    <td className="border px-2 py-3 text-center align-middle whitespace-nowrap">
                      {record.employeeCode || "-"}
                    </td>
                    <td className="border px-2 py-3 align-middle">
                      <button
                        onClick={() => openWorkingWithWindow(record.employeeId)}
                        className="underline text-blue-600 hover:text-blue-800"
                      >
                        {record.employeeName}
                      </button>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div className="whitespace-normal">
                        {record.designation}
                      </div>
                      <div className="whitespace-normal">{record.division}</div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div className="whitespace-normal">{record.state}</div>
                      <div className="whitespace-normal">{record.substate}</div>
                      <div className="whitespace-normal">
                        {record.headquarter}
                      </div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle whitespace-nowrap">
                      {record.final_joining_date === "-"
                        ? "-"
                        : formatDate(record.final_joining_date) || "-"}
                    </td>
                    <td className="border px-2 py-3 align-middle text-center">
                      {record.manager}
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div className="flex flex-col items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600 mb-1" />
                        {record.mandatoryCanLogin ? (
                          <img
                            src={GreenFlag}
                            alt="can-login"
                            className="w-6 h-6 mt-1"
                          />
                        ) : (
                          <img
                            src={RedFlag}
                            alt="cannot-login"
                            className="w-6 h-6 mt-1"
                          />
                        )}
                      </div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() =>
                            openDcrPreviewWindow(record.employeeId)
                          }
                          className="underline text-blue-600 hover:text-blue-800"
                        >
                          <span className="whitespace-nowrap">
                            {record.visits}
                          </span>
                        </button>
                        <div className="border-t border-gray-200 w-full my-1" />
                        <button
                          onClick={() =>
                            openDcrPreviewWindow(record.employeeId)
                          }
                          className="underline text-blue-600 hover:text-blue-800"
                        >
                          <span className="whitespace-nowrap">
                            {typeof record.pob === "number"
                              ? record.pob.toLocaleString()
                              : record.pob}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() =>
                            openLocationHistoryWindow(
                              record.employeeId,
                              record.employeeName,
                            )
                          }
                          className="underline text-blue-600 hover:text-blue-800 font-bold"
                        >
                          <span className="whitespace-nowrap">
                            {record.locationCount || 0}
                          </span>
                        </button>
                        <div className="border-t border-gray-200 w-full my-1" />
                        <button
                          onClick={() => handleViewMap(record)}
                          className="underline text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          View
                        </button>
                      </div>
                    </td>
                    <td className="border px-2 py-3 align-middle max-w-[200px] text-xs">
                      <button
                        onClick={() => handleViewMap(record)}
                        className="underline text-blue-600 hover:text-blue-800 whitespace-normal break-words"
                      >
                        {record.latestLocation || "-"}
                      </button>
                      <div className="text-bold text-red-600 mt-1">
                        {formatDateTime(record.gps_stop_idle_time, true)}
                      </div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle bg-red-100 whitespace-nowrap">
                      {record.morningPunchTime ? (
                        <button
                          onClick={() => handleMorningPunchMap(record)}
                          className="underline text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          {formatTimeTo12Hour(record.morningPunchTime)}
                        </button>
                      ) : (
                        <span className="inline-block whitespace-nowrap">
                          -
                        </span>
                      )}
                      <div className="border-t border-gray-200 w-full my-1" />
                      {record.eveningPunchTime ? (
                        <button
                          onClick={() => handleEveningPunchMap(record)}
                          className="underline text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          {formatTimeTo12Hour(record.eveningPunchTime)}
                        </button>
                      ) : (
                        <span className="inline-block whitespace-nowrap">
                          -
                        </span>
                      )}
                    </td>
                    <td className="border px-2 py-3 text-center align-middle whitespace-normal text-xs">
                      <div>{formatDateTime(record.firstCallTime),true}</div>

                      
                      <div className="border-t border-gray-200 w-full my-1" />
                      <div>{formatTimeTo12Hour(record.lastCallTime)}</div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle whitespace-nowrap">
                      <div>{record.morningFieldBy || "-"}</div>
                      <div>{record.eveningFieldBy || "-"}</div>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle">
                      <div>
                        <span className="text-red-600 font-semibold">
                          {record.morningStatus}
                        </span>
                        <br />
                        <span className="text-red-600 font-semibold">
                          {record.eveningStatus}
                        </span>
                      </div>
                    </td>
                    <td className="border px-2 py-3 align-middle text-xs">
                      <button
                        onClick={() => openMapModal(record, "homeOnly")}
                        className="underline text-blue-600 hover:text-blue-800"
                      >
                        <span className="whitespace-normal break-words">
                          {record.residenceAddress}
                        </span>
                      </button>
                    </td>
                    <td className="border px-2 py-3 text-center align-middle whitespace-nowrap">
                      <button
                        onClick={() => openSmsModal(record)}
                        className="underline text-blue-600 hover:text-blue-800"
                      >
                        {record.smsStatus}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-red-600 font-bold text-sm text-white">
                  <td colSpan={8} className="border px-2 py-3 text-center">
                    TOTAL ABSENT: {tableData.length}
                  </td>
                  <td className="border px-2 py-3 text-center">
                    <div>{footerTotals.totalVisits}</div>
                    <div className="border-t border-gray-400 my-1" />
                    <div>{footerTotals.totalPob.toLocaleString()}</div>
                  </td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center bg-red-200">
                    <div>Punch : {footerTotals.punch}</div>
                    <div className="border-t border-gray-400 my-1" />
                    <div>No Punch : {footerTotals.noPunch}</div>
                  </td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center">-</td>
                  <td className="border px-2 py-3 text-center">-</td>
                </tr>
              </tfoot>
            </table>

            <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>
          </div>
        )}

        {!isLoading && tableData.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No absent employees found for the selected filters.
          </div>
        )}

        {smsModal.isOpen && smsModal.employee && (
          <SmsModal
            isOpen={smsModal.isOpen}
            name={smsModal.employee.employeeName}
            phone={smsModal.employee.phone}
            onSend={async (payload) => {
              try {
                await axios.post(`${BASE_URL}/api/send-sms`, payload, {
                  headers: getAuthHeaders(),
                });
              } catch (err) {
                console.error("SMS send failed", err);
                throw err;
              }
            }}
            onClose={() => setSmsModal({ isOpen: false, employee: null })}
          />
        )}

        {mapModal.isOpen && mapModal.employeeData && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2">
            <div className="bg-white rounded-lg w-full max-w-7xl h-[95vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b bg-white sticky top-0 z-10">
                <h3 className="text-xl font-bold text-gray-800">
                  Employee Location Tracker -{" "}
                  {mapModal.employeeData.employeeName}
                  <span className="text-sm text-gray-500 ml-2">
                    (Date: {mapModal.employeeData.trackDate})
                  </span>
                </h3>
                <button
                  onClick={closeMapModal}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold bg-gray-100 hover:bg-gray-200 w-10 h-10 rounded-full flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 p-1 overflow-hidden">
                <EmployeeMapTracker
                  key={`${mapModal.employeeData.employeeId}-${mapModal.employeeData.trackDate}-${mapModal.employeeData.context}`}
                  employeeId={mapModal.employeeData.employeeId}
                  trackDate={mapModal.employeeData.trackDate}
                  employeeName={mapModal.employeeData.employeeName}
                  residenceAddress={mapModal.employeeData.residenceAddress} 
                  specificPunchTime={mapModal.employeeData.specificPunchTime} 
                  onClose={closeMapModal}
                  context={mapModal.employeeData.context}
                  isModal={true}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AbsentDailyPunch;
